import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, Newspaper, Target, FileText, TrendingUp, Lock, ArrowLeft,
  Upload, Image, FileType, X, Sparkles, Camera
} from "lucide-react";

const PrelimsNewspaperConverterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [result, setResult] = useState(null);
  const [inputMode, setInputMode] = useState("file"); // Default to file for newspaper
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const isPro = user?.role === "PRO";

  // PRO Access Check - Redirect FREE users to upgrade page
  useEffect(() => {
    if (user && !isPro) {
      toast({
        variant: "destructive",
        title: "PRO Feature",
        description: "Newspaper Converter is a PRO feature. Please upgrade to access."
      });
      navigate("/upgrade");
    }
  }, [user, isPro, navigate, toast]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          description: "Unsupported file type. Please upload PDF, PNG, JPG, WEBP, or TXT files."
        });
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: "destructive",
          description: "File too large. Maximum size is 10MB."
        });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleConvertFile = async () => {
    if (!selectedFile) {
      toast({
        variant: "destructive",
        description: "Please select a file to upload"
      });
      return;
    }

    setLoading(true);
    setResult(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await managedAxios.post(
        `${API_BASE}/converter/newspaper-to-mcq/upload`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      setResult(response.data);
      toast({
        description: `Extracted ${response.data.mcqs?.length || 0} MCQs from ${selectedFile.name}!`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to convert file to MCQs"
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleConvertText = async () => {
    if (!content.trim()) {
      toast({
        variant: "destructive",
        description: "Please paste newspaper article content"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/newspaper-to-mcq`,
        {
          content
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Extracted ${response.data.mcqs?.length || 0} MCQs from article!`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to convert article to MCQs"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = () => {
    if (inputMode === "file") {
      handleConvertFile();
    } else {
      handleConvertText();
    }
  };

  const sampleArticle = `India's Nuclear Energy Milestone

India has achieved a significant milestone in its clean energy transition, with nuclear power generation reaching a record 47,112 million units in 2023-24, representing a 25% increase over the previous year. The government aims to triple nuclear power capacity from 7,480 MW to 22,480 MW by 2031-32.

Key Developments:
• Jaitapur Nuclear Power Project (Maharashtra): World's largest nuclear power plant being developed with French cooperation (6 x 1,650 MW EPR reactors)
• Kudankulam Units 5 & 6 (Tamil Nadu): Under construction with Russian collaboration
• Indigenous Pressurized Heavy Water Reactors (PHWRs): 16 reactors operational with 220/540/700 MW capacity
• Fast Breeder Reactor Programme: Prototype Fast Breeder Reactor (PFBR) at Kalpakkam commissioned

Strategic Significance:
India's nuclear energy push aligns with its commitment to achieve 500 GW non-fossil fuel capacity by 2030 and net-zero emissions by 2070 (announced at COP26). Nuclear power currently contributes 3% to India's electricity mix but is crucial for baseload power as solar/wind are intermittent.

Challenges:
- High capital costs and long gestation periods
- Public concerns about safety post-Fukushima incident
- Limited domestic uranium reserves (requires imports from Kazakhstan, Canada)
- Nuclear liability issues deterring foreign investment

Policy Framework:
The Atomic Energy Act, 1962 maintains government monopoly in nuclear power. Recent amendments allow private sector participation in select areas. Civil Nuclear Liability Act, 2010 addresses supplier liability concerns.`;

  const getFileIcon = (file) => {
    if (!file) return <Upload className="w-8 h-8" />;
    if (file.type.startsWith('image/')) return <Image className="w-8 h-8 text-blue-500" />;
    if (file.type === 'application/pdf') return <FileType className="w-8 h-8 text-red-500" />;
    return <FileText className="w-8 h-8 text-slate-500" />;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Newspaper → MCQ Converter
          </h1>
          <p className="text-sm text-slate-600">
            Extract UPSC-relevant MCQs from newspaper articles
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-red-500"}>
          {isPro ? "⭐ PRO" : "🔒 PRO ONLY"}
        </Badge>
      </div>

      {/* PRO-Only Lock Screen */}
      {!isPro && (
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300">
          <CardContent className="p-12">
            <div className="text-center space-y-4">
              <Lock className="w-16 h-16 mx-auto text-amber-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                PRO Feature Locked
              </h2>
              <p className="text-slate-700 max-w-lg mx-auto">
                Upload newspaper photos/PDFs or paste articles from The Hindu, Indian Express, PIB, and PRS. 
                Get 3-10 MCQs with elimination logic, related facts, mains questions, and PYQ predictions.
              </p>
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                <Badge variant="outline" className="bg-white">
                  <Camera className="w-3 h-3 mr-1" /> Photo Upload
                </Badge>
                <Badge variant="outline" className="bg-white">
                  <FileType className="w-3 h-3 mr-1" /> PDF Support
                </Badge>
                <Badge variant="outline" className="bg-white">
                  <Sparkles className="w-3 h-3 mr-1" /> AI OCR
                </Badge>
              </div>
              <div className="pt-4">
                <Button 
                  onClick={() => navigate('/upgrade')}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-8"
                >
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Interface (PRO) */}
      {isPro && (
        <>
          {/* Info Card */}
          <Card className="bg-gradient-to-r from-fuchsia-50 to-pink-50 border-fuchsia-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Newspaper className="w-5 h-5 text-fuchsia-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-fuchsia-900 mb-1">How it works:</p>
                  <ul className="text-sm text-slate-700 space-y-1">
                    <li>• <strong>Upload photos</strong> of newspaper articles or PDFs from The Hindu, Indian Express, PIB, PRS</li>
                    <li>• AI extracts text using OCR and identifies UPSC-relevant factual content</li>
                    <li>• Generate 5-25 MCQs with elimination logic, related facts, and mains questions</li>
                    <li>• Get 2 PYQ-linked predictions for upcoming exams</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <Card>
              <CardContent className="p-6 space-y-4">
                {/* Input Mode Toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setInputMode("file")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === "file" 
                        ? "bg-white shadow text-slate-900" 
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload File
                  </button>
                  <button
                    onClick={() => setInputMode("text")}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === "text" 
                        ? "bg-white shadow text-slate-900" 
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Paste Text
                  </button>
                </div>

                {/* File Upload Mode */}
                {inputMode === "file" && (
                  <div>
                    <Label className="mb-2 block">Upload Newspaper Article</Label>
                    
                    {/* Drop Zone */}
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        selectedFile 
                          ? "border-fuchsia-400 bg-fuchsia-50" 
                          : "border-slate-300 hover:border-fuchsia-400 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.txt"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={loading}
                      />
                      
                      {selectedFile ? (
                        <div className="space-y-3">
                          <div className="w-16 h-16 mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center">
                            {getFileIcon(selectedFile)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{selectedFile.name}</p>
                            <p className="text-sm text-slate-500">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(null);
                            }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-fuchsia-100 to-pink-100 rounded-xl flex items-center justify-center">
                            <Camera className="w-8 h-8 text-fuchsia-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-700">Upload newspaper photo or PDF</p>
                            <p className="text-sm text-slate-500">PNG, JPG, WEBP, PDF, or TXT (max 10MB)</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Upload Progress */}
                    {loading && uploadProgress > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-slate-600 mb-1">
                          <span>Processing...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-fuchsia-500 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="mt-4 p-3 bg-gradient-to-r from-fuchsia-50 to-pink-50 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <Sparkles className="w-3 h-3 inline mr-1 text-fuchsia-500" />
                        <strong>Pro Tip:</strong> Take a clear photo of the newspaper article. 
                        Our AI OCR will extract all text and generate UPSC MCQs automatically!
                      </p>
                    </div>
                  </div>
                )}

                {/* Text Input Mode */}
                {inputMode === "text" && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="content">Article Content</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setContent(sampleArticle)}
                        disabled={loading}
                      >
                        Load Sample
                      </Button>
                    </div>
                    <Textarea
                      id="content"
                      placeholder="Paste newspaper article here (editorials, news analysis, policy updates)"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      disabled={loading}
                      rows={16}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      {content.length} characters • Max 30,000 • {content.length > 0 ? `Suggested MCQs: ${Math.max(5, Math.min(25, Math.floor(content.length / 1000)))}` : ''}
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleConvert}
                  disabled={loading || (inputMode === "file" && !selectedFile) || (inputMode === "text" && !content.trim())}
                  className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {inputMode === "file" ? "Extracting & Processing..." : "Extracting MCQs..."}
                    </>
                  ) : (
                    <>
                      <Target className="w-4 h-4 mr-2" />
                      Extract UPSC MCQs
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Output Section */}
            <div className="space-y-4">
              {result && (
                <>
                  {/* Source File Info */}
                  {result.source_file && (
                    <Card className="bg-fuchsia-50 border-fuchsia-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <Newspaper className="w-5 h-5 text-fuchsia-600" />
                          <p className="text-sm font-medium text-slate-900">
                            Extracted from: {result.source_file}
                          </p>
                        </div>
                        {result.extracted_text_preview && (
                          <p className="text-xs text-slate-500 mt-2 line-clamp-3">
                            {result.extracted_text_preview}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Extracted Points */}
                  {result.extracted_points && result.extracted_points.length > 0 && (
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-semibold text-green-900">
                            UPSC-Relevant Points Extracted ({result.extracted_points.length})
                          </p>
                        </div>
                        <ul className="space-y-1">
                          {result.extracted_points.map((point, idx) => (
                            <li key={idx} className="text-sm text-slate-700 flex gap-2">
                              <span className="text-green-600">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* MCQs */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-fuchsia-600" />
                      <h3 className="font-semibold text-slate-900">
                        Generated MCQs ({result.mcqs?.length || 0})
                      </h3>
                    </div>

                    {result.mcqs?.map((mcq, idx) => (
                      <Card key={idx} className="border-l-4 border-l-fuchsia-500">
                        <CardContent className="p-4 space-y-3">
                          {/* Question */}
                          <div className="flex items-start gap-2">
                            <Badge variant="outline" className="shrink-0">Q{idx + 1}</Badge>
                            <p className="text-sm font-medium text-slate-900">{mcq.question}</p>
                          </div>

                          {/* Options */}
                          <div className="space-y-1 pl-12">
                            {Object.entries(mcq.options).map(([key, value]) => (
                              <div key={key} className="flex gap-2 text-sm">
                                <span className="font-semibold">{key})</span>
                                <span>{value}</span>
                              </div>
                            ))}
                          </div>

                          {/* Answer & Explanation */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                            <p className="text-xs font-semibold text-blue-900">
                              Answer: {mcq.correct_answer}
                            </p>
                            <p className="text-xs text-slate-700">{mcq.explanation}</p>

                            {/* Elimination Logic */}
                            {mcq.elimination_logic && (
                              <div className="pt-2 border-t border-blue-200">
                                <p className="text-xs font-semibold text-blue-900 mb-1">
                                  Elimination Logic:
                                </p>
                                {Object.entries(mcq.elimination_logic).map(([key, value]) => (
                                  <p key={key} className="text-xs text-slate-600">
                                    • {key}: {value}
                                  </p>
                                ))}
                              </div>
                            )}

                            {/* Related Facts */}
                            {mcq.related_facts && mcq.related_facts.length > 0 && (
                              <div className="pt-2 border-t border-blue-200">
                                <p className="text-xs font-semibold text-blue-900 mb-1">
                                  Related Facts:
                                </p>
                                {mcq.related_facts.map((fact, i) => (
                                  <p key={i} className="text-xs text-slate-600">
                                    • {fact}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Mains Questions */}
                  {result.mains_questions && result.mains_questions.length > 0 && (
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="p-4 space-y-2">
                        <p className="text-sm font-semibold text-purple-900 mb-2">
                          📝 Mains Questions from this Article
                        </p>
                        {result.mains_questions.map((q, idx) => (
                          <div key={idx} className="text-sm text-slate-700">
                            {idx + 1}. {q}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* PYQ Predictions */}
                  {result.pyq_predictions && result.pyq_predictions.length > 0 && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4 space-y-2">
                        <p className="text-sm font-semibold text-amber-900 mb-2">
                          🎯 PYQ-Linked Predictions
                        </p>
                        {result.pyq_predictions.map((pred, idx) => (
                          <div key={idx} className="text-sm text-slate-700">
                            {idx + 1}. {pred}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Reset Button */}
                  <Button
                    onClick={() => {
                      setResult(null);
                      setContent("");
                      setSelectedFile(null);
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Convert New Article
                  </Button>
                </>
              )}

              {/* Placeholder */}
              {!result && !loading && (
                <Card className="border-dashed">
                  <CardContent className="p-12">
                    <div className="text-center text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Extracted MCQs will appear here</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PrelimsNewspaperConverterPage;
